import React from "react";
import { useAuth } from "../context/AuthContext";
import { discordLoginUrl } from "../lib/api";
import { readPrefs, writePrefs } from "../lib/prefs";

export function AuthModal({ open, onClose }) {
  const { loginGoogle } = useAuth();
  if (!open) return null;
  const lastProvider = readPrefs().last_provider;
  // When we know the provider, lead with that single button + show the
  // other as a smaller "try a different way" link. Cuts decision time
  // dramatically for returning visitors.
  const usedDiscord = lastProvider === "discord";
  const usedGoogle  = lastProvider === "google";

  const goDiscord = () => { writePrefs({ last_provider: "discord" }); window.location.href = discordLoginUrl; };
  const goGoogle  = () => { writePrefs({ last_provider: "google" });  loginGoogle(); };

  const DiscordBtn = ({ primary = true }) => (
    <button
      onClick={goDiscord}
      data-testid="auth-discord-btn"
      className={primary
        ? "w-full mb-3 py-4 rounded-2xl flex items-center justify-center gap-3 font-bold text-white"
        : "w-full text-[12px] tracking-widest text-white/65 hover:text-white py-2"
      }
      style={primary ? {
        background: "linear-gradient(90deg, #5865F2, #4752c4)",
        boxShadow: "0 0 16px rgba(88,101,242,0.6)",
      } : undefined}
    >
      <i className="fab fa-discord text-xl" />
      {primary ? "CONTINUE WITH DISCORD" : "Use Discord instead"}
    </button>
  );

  const GoogleBtn = ({ primary = false }) => (
    <button
      onClick={goGoogle}
      data-testid="auth-google-btn"
      className={primary
        ? "w-full mb-3 py-4 rounded-2xl flex items-center justify-center gap-3 font-bold bg-white text-gray-900 hover:scale-[1.02] transition-transform"
        : "w-full text-[12px] tracking-widest text-white/65 hover:text-white py-2"
      }
    >
      <i className={`fab fa-google text-xl ${primary ? "text-[#DB4437]" : "text-white/65"}`} />
      {primary ? "CONTINUE WITH GOOGLE" : "Use Google instead"}
    </button>
  );

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-md p-4"
      onClick={onClose}
      data-testid="auth-modal-backdrop"
    >
      <div
        className="glass neon-glow rounded-3xl p-8 max-w-md w-full"
        onClick={(e) => e.stopPropagation()}
        data-testid="auth-modal"
      >
        <div className="flex items-center justify-between mb-6">
          <div>
            <p className="text-xs tracking-[3px] text-[#14b8a6]">LEAGUE HUB</p>
            <h2 className="text-3xl font-display grad-text">
              {lastProvider ? "WELCOME BACK" : "SIGN IN"}
            </h2>
          </div>
          <button
            onClick={onClose}
            data-testid="auth-modal-close"
            className="text-white/60 hover:text-white text-2xl"
          >
            <i className="fas fa-times" />
          </button>
        </div>

        {lastProvider ? (
          <p data-testid="auth-modal-last-provider-hint"
             className="text-[#a3a3ac] mb-6">
            Picking up where you left off — you signed in with{" "}
            <b className="text-white">{lastProvider === "discord" ? "Discord" : "Google"}</b> last time.
          </p>
        ) : (
          <p className="text-[#a3a3ac] mb-6">
            Pick a portal to enter the arena. Your profile, leagues, and badges follow you across both.
          </p>
        )}

        {/* Primary CTA — the one they used last time, or Discord as default */}
        {usedGoogle ? <GoogleBtn primary /> : <DiscordBtn primary />}
        {/* Secondary — small link */}
        {usedGoogle ? <DiscordBtn primary={false} /> : <GoogleBtn primary={!lastProvider} />}

        <p className="text-center text-[11px] text-white/55 mt-6 leading-relaxed">
          By continuing you agree to our{" "}
          <a
            href="/legal/terms"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="auth-modal-terms-link"
            className="underline text-[#14b8a6] hover:text-[#10a39a]"
          >
            Terms of Service
          </a>{" "}
          and{" "}
          <a
            href="/legal/privacy"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="auth-modal-privacy-link"
            className="underline text-[#14b8a6] hover:text-[#10a39a]"
          >
            Privacy Policy
          </a>. Play fair, respect fellow operators.
        </p>
      </div>
    </div>
  );
}

export default AuthModal;
